import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema, generateArticleSchema } from "@/lib/jsonld";

// Images
import coverImage from "@/assets/blog/notifications-push-wallet/couverture.webp";
import schemaImage from "@/assets/blog/notifications-push-wallet/schema-fonctionnement.webp";
import comparatifImage from "@/assets/blog/notifications-push-wallet/comparatif-taux-ouverture.webp";
import infographieImage from "@/assets/blog/notifications-push-wallet/infographie-statistiques.webp";
import casUsageImage from "@/assets/blog/notifications-push-wallet/cas-usage.webp";

const YOUTUBE_VIDEO_ID = "ZOEzPVUk8Hs";

const NotificationsPushWallet = () => {
  const jsonLdSchemas = [
    generateWebPageSchema(
      "Notifications push wallet : le canal de fidélisation qui cartonne en 2026",
      seoConfig.blogNotificationsPushWallet.description,
      "/notifications-push-wallet-fidelisation"
    ),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: "Notifications push wallet", path: "/notifications-push-wallet-fidelisation" }
    ]),
    generateArticleSchema(
      "Notifications push wallet : le canal de fidélisation qui cartonne en 2026",
      seoConfig.blogNotificationsPushWallet.description,
      "https://unotif.com/notifications-push-wallet-fidelisation",
      "2026-03-23"
    )
  ];

  const imageStyle = {
    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.blogNotificationsPushWallet} jsonLd={jsonLdSchemas} />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="text-sm font-medium text-[#495456] mb-6">
              <Link to="/blog" className="text-[#0c97a9] hover:underline">Blog</Link>
              <span className="mx-2">›</span>
              <span className="text-[#074155]">Notifications push wallet</span>
            </nav>

            {/* H1 */}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-8">
              Notifications push wallet : le canal de fidélisation qui cartonne en 2026
            </h1>

            {/* Cover Image */}
            <img
              src={coverImage}
              alt="Illustration des notifications push wallet sur smartphone avec écran verrouillé et message marketing visible en 2026"
              loading="eager"
              className="rounded-2xl w-full h-auto"
              style={imageStyle}
            />
          </div>
        </section>

        {/* Article Content */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Introduction */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Votre dernier email marketing a été ouvert par 25 % de vos destinataires, au mieux. Votre dernier SMS a irrité une partie des clients qui n'avaient pas envie de le recevoir ce jour-là. Quant à votre application mobile, la moitié de vos clients ne l'ont pas téléchargée.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Il existe un quatrième canal que la plupart des commerçants ignorent encore : les <strong className="font-semibold">notifications push wallet</strong>, envoyées via Apple Wallet et Google Wallet. Taux d'ouverture à 53 %, sans algorithme, sans spam folder, sans application à installer. En 2026, <strong className="font-semibold">37,8 millions de cartes wallet sont actives en France</strong>.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Cet article explique comment ce canal fonctionne, ce qu'il produit concrètement, et comment le mettre en place en moins d'une semaine. Si vous cherchez d'abord à situer le wallet parmi tous les outils disponibles, notre guide sur les <Link to="/meilleurs-outils-de-fidelisation-client" className="text-[#0c97a9] hover:underline font-semibold">outils de fidélisation client</Link> couvre le terrain complet.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* H2 - Qu'est-ce qu'une notification push wallet ? */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Qu'est-ce qu'une notification push wallet ?
            </h2>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              Le wallet mobile : déjà dans la poche de vos clients
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Apple Wallet</strong> sur iPhone et <strong className="font-semibold">Google Wallet</strong> sur Android sont des applications préinstallées sur tous les smartphones vendus aujourd'hui. Pas besoin de les chercher sur l'App Store : elles sont là dès la sortie de la boîte.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ces applications stockent des documents numériques : carte d'embarquement, billet de concert, ticket de cinéma, <strong className="font-semibold">carte de fidélité</strong>. Le client les consulte sans connexion internet, directement depuis son écran verrouillé. Pour un commerçant, c'est l'emplacement le plus précieux qui soit : la poche de son client, à portée de regard à tout moment de la journée.
            </p>

            {/* Image schema fonctionnement */}
            <img
              src={schemaImage}
              alt="Schéma du fonctionnement des notifications Wallet en 3 étapes, du tableau de bord au smartphone sur écran verrouillé"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              Comment fonctionne le push wallet concrètement
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Voici le mécanisme exact. Le client ajoute votre carte dans son wallet en scannant un QR code affiché en caisse, ou en cliquant sur un lien partagé par email ou sur les réseaux. Cette action lui attribue un identifiant unique lié à son téléphone. Dès lors, vous lui envoyez une <strong className="font-semibold">notification push</strong> depuis votre plateforme : le message arrive directement sur son écran verrouillé, comme un SMS, mais sans le coût et sans la complexité de l'opt-in réglementaire.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La carte elle-même se met à jour dynamiquement. Le solde de points s'actualise à chaque visite enregistrée. Une offre du moment apparaît sur la face avant de la carte. Un message d'anniversaire s'affiche automatiquement le bon jour. Tout cela sans que le client n'ouvre une application, ne se connecte à un compte ou ne fasse quoi que ce soit.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-[#022226] mb-3 mt-8">
              Push wallet vs notification push d'application : la différence clé
            </h3>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Une application mobile demande un téléchargement. Sur le marché grand public, le <strong className="font-semibold">taux d'adoption d'une app de fidélité</strong> oscille entre 15 et 30 % selon les secteurs. Autrement dit, 70 à 85 % de vos clients ne téléchargent pas votre application, et ne reçoivent donc aucune notification.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Avec le wallet, ce frein disparaît. Le client scanne un QR code en dix secondes, confirme d'un tap, et la carte est ajoutée. Pas de compte à créer, pas de mot de passe, pas de mise à jour à installer. Le <strong className="font-semibold">taux d'adoption d'une carte wallet</strong> dans un commerce local actif dépasse régulièrement 60 %. La différence de volume joignable est immédiate.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* H2 - Pourquoi le wallet surpasse l'email et le SMS */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Pourquoi le wallet surpasse l'email et le SMS pour la fidélisation
            </h2>

            {/* Image comparatif */}
            <img
              src={comparatifImage}
              alt="Graphique comparatif des taux d'ouverture email, push app, push wallet et SMS avec coûts et performances"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Chaque canal de communication a ses avantages et ses contraintes. Voici la comparaison complète :
            </p>

            {/* Tableau comparatif */}
            <div className="overflow-x-auto my-8">
              <table className="w-full border-collapse text-[13px] sm:text-sm">
                <thead>
                  <tr className="bg-[#074155] text-white">
                    <th className="px-4 py-3 text-left font-semibold rounded-tl-xl">Critère</th>
                    <th className="px-4 py-3 text-left font-semibold">Email</th>
                    <th className="px-4 py-3 text-left font-semibold">SMS</th>
                    <th className="px-4 py-3 text-left font-semibold">Push app</th>
                    <th className="px-4 py-3 text-left font-semibold rounded-tr-xl">Push wallet</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-[#495456]">Taux d'ouverture</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">23 à 30 %</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">&gt; 90 %</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">40 à 50 %</td>
                    <td className="px-4 py-3 font-semibold text-[#074155]">53 %</td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-[#495456]">Friction côté client</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Faible</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Faible</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Forte</td>
                    <td className="px-4 py-3 font-semibold text-[#074155]">Très faible</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-[#495456]">Risque spam ou opt-out</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Élevé</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Élevé</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Moyen</td>
                    <td className="px-4 py-3 font-semibold text-[#074155]">Faible</td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-[#495456]">Personnalisation dynamique</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Oui</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Non</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Oui</td>
                    <td className="px-4 py-3 font-semibold text-[#074155]">Oui</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-[#495456]">Coût par message</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Faible</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">0,05 à 0,10 €</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Faible</td>
                    <td className="px-4 py-3 font-semibold text-[#074155]">Faible</td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-[#495456]">Dépendance à un algorithme</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Non</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Non</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Non</td>
                    <td className="px-4 py-3 font-semibold text-[#074155]">Non</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-[#495456]">Taux d'adoption client</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Élevé</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Moyen</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Faible</td>
                    <td className="px-4 py-3 font-semibold text-[#074155]">Élevé</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le SMS affiche un taux d'ouverture supérieur à 90 %, mais son coût par message, son risque de désabonnement et son caractère intrusif le réservent aux communications à forte valeur temporelle. Le <strong className="font-semibold">push wallet</strong> combine un taux d'ouverture élevé avec une friction quasi nulle et un coût marginal faible : les trois conditions réunies pour un canal durable, pas seulement performant à court terme.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* H2 - Les chiffres */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Les chiffres qui prouvent l'efficacité du wallet en 2026
            </h2>

            {/* Image infographie */}
            <img
              src={infographieImage}
              alt="Infographie des statistiques clés sur l'usage du wallet en France avec adoption, rétention et impact sur les achats"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les données issues du baromètre Captain Wallet 2026 dressent un tableau précis :
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">37,8 millions de cartes wallet actives en France.</strong> Ce chiffre représente les cartes réellement utilisées, pas simplement installées. La base est réelle, ancrée dans les usages quotidiens, et elle progresse chaque trimestre.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">47 % des Français déclarent être influencés par les notifications push wallet</strong> dans leur décision de se rendre dans un commerce. Ce taux a progressé de 5 points en un an. L'habitude de consulter et d'agir sur ces notifications est en train de s'ancrer dans les comportements d'achat.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">90 % de taux de rétention</strong> des cartes wallet chez les marques qui les utilisent activement. Face au taux moyen d'abandon de 58 % sur les programmes de fidélité classiques, l'écart dit tout sur l'engagement réel que génère ce canal.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">+37 % de fréquence d'achat</strong> pour les clients ayant une carte wallet active, par rapport aux clients sans carte. Ce chiffre mesuré sur des commerces locaux traduit directement l'impact sur le chiffre d'affaires, pas seulement sur la satisfaction déclarée.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">71 % des Français</strong> reconnaissent utiliser leur wallet mobile au quotidien. Le comportement est installé, et l'ouverture aux messages commerciaux qui passent par ce canal l'est aussi.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* H2 - Quels types de messages */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Quels types de messages envoyer via le wallet pour fidéliser ?
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le format le plus performant est celui qui s'intègre naturellement dans la vie du client. Un message trop commercial, trop fréquent ou mal ciblé génère des désactivations. Cinq types de messages produisent des résultats mesurables :
            </p>

            {/* Tableau types de messages */}
            <div className="overflow-x-auto my-8">
              <table className="w-full border-collapse text-[13px] sm:text-sm">
                <thead>
                  <tr className="bg-[#074155] text-white">
                    <th className="px-4 py-3 text-left font-semibold rounded-tl-xl">Type de message</th>
                    <th className="px-4 py-3 text-left font-semibold">Exemple de texte</th>
                    <th className="px-4 py-3 text-left font-semibold rounded-tr-xl">Objectif</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-[#495456]">Rappel de points</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">"Il vous reste 40 points. Encore 2 visites et votre récompense est là."</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Réengagement actif</td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-[#495456]">Offre flash géolocalisée</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">"Vous êtes près de chez nous ? Aujourd'hui : -20 % sur toute la carte."</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Trafic immédiat</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-[#495456]">Message anniversaire</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">"Bonne fête. Un cadeau vous attend à votre prochaine visite."</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Fidélisation émotionnelle</td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-[#495456]">Réactivation client inactif</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">"Ça fait 6 semaines. Une offre exclusive vous attend."</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Récupération client perdu</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-[#495456]">Événement exclusif membres</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">"Vente privée demain à 18h. Réservé à notre communauté."</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Sentiment d'appartenance</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La <strong className="font-semibold">géolocalisation</strong> mérite une mention à part. Apple Wallet et Google Wallet permettent d'envoyer une notification au moment exact où le client passe à proximité de votre commerce, sans que vous ayez à déclencher l'envoi manuellement. Le système détecte la présence du téléphone dans un rayon défini et envoie l'alerte. C'est un déclencheur comportemental que ni l'email ni le SMS n'atteignent avec cette précision et cette fluidité.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* H2 - Comment mettre en place */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Comment mettre en place des notifications push wallet en moins d'une semaine
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le frein technique est réel dans l'imaginaire des commerçants. Il ne l'est pas dans les faits. Mettre en place une carte wallet opérationnelle suit quatre étapes :
            </p>

            {/* Tableau étapes */}
            <div className="overflow-x-auto my-8">
              <table className="w-full border-collapse text-[13px] sm:text-sm">
                <thead>
                  <tr className="bg-[#074155] text-white">
                    <th className="px-4 py-3 text-left font-semibold rounded-tl-xl">Étape</th>
                    <th className="px-4 py-3 text-left font-semibold">Action concrète</th>
                    <th className="px-4 py-3 text-left font-semibold rounded-tr-xl">Temps estimé</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-[#495456]">1</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Créer la carte : logo, couleurs, informations affichées (nom du commerce, offre en cours, solde de points)</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">30 à 60 minutes</td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-[#495456]">2</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Générer le QR code et le lien d'installation, à afficher en caisse, sur les réseaux sociaux, en signature d'email</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">10 minutes</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-[#495456]">3</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Présenter le QR code aux clients avec une phrase simple : "Ajoutez notre carte fidélité directement dans votre téléphone."</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Continu dès le premier jour</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-[#495456]">4</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Envoyer la première notification push depuis le tableau de bord : message de bienvenue ou première offre exclusive membres</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">5 minutes</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">La carte est opérationnelle en moins de 24 heures.</strong> Pas de développeur, pas de budget technique. Des plateformes comme Unotif permettent de créer la carte, de générer le QR code et d'envoyer les premières notifications depuis une interface accessible à n'importe quel commerçant ou entrepreneur, sans compétences numériques particulières.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La question n'est pas de savoir si vous êtes capable de mettre ça en place. C'est de savoir combien de clients vous voulez pouvoir joindre directement la semaine prochaine.
            </p>


            {/* H2 - Qui bénéficie le plus */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Qui bénéficie le plus du wallet marketing ?
            </h2>

            {/* Image cas d'usage */}
            <img
              src={casUsageImage}
              alt="Tableau des cas d'usage du wallet pour commerce local, coach et e-commerce avec bénéfices marketing associés"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">wallet marketing</strong> s'adapte à des contextes très différents, avec des cas d'usage distincts selon les profils.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">commerce local</strong> (café, boutique, salon, restaurant) tire un avantage immédiat du tampon numérique couplé aux push géolocalisés. Un client qui passe devant le commerce reçoit une alerte sur son écran verrouillé. Ce déclencheur contextuel génère un trafic additionnel mesurable dès les premières semaines, sans campagne publicitaire.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">coach ou formateur</strong> l'utilise différemment : distribuer des ressources exclusives à ses abonnés, notifier la sortie d'un nouveau module, rappeler une session live imminente. Le wallet devient un canal de communication direct avec la communauté, sans dépendre des algorithmes des réseaux sociaux qui limitent la portée organique à 3 ou 5 % de l'audience.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              L'<strong className="font-semibold">e-commerce</strong> relie la carte wallet au compte client et l'utilise pour des relances de paniers abandonnés, des alertes de réassort sur des produits consultés, ou des offres de fidélité personnalisées par segment d'achat.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* YouTube Video Embed */}
            <div className="my-8">
              <div
                className="relative w-full overflow-hidden rounded-2xl"
                style={{
                  aspectRatio: "16/9",
                  ...imageStyle,
                }}
              >
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${YOUTUBE_VIDEO_ID}?rel=0&modestbranding=1`}
                  title="Démonstration notifications push wallet"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full border-0"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Conclusion */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              En 2026, <strong className="font-semibold">37,8 millions de cartes wallet sont actives en France</strong>. Le comportement est installé, l'infrastructure est là, et la grande majorité des petites structures n'utilisent pas encore ce canal. Pour ceux qui s'y mettent maintenant, l'avantage est clair : une base de clients joignables directement, sans dépendre d'un algorithme, sans coût par message variable, avec un taux d'engagement que l'email et les réseaux sociaux n'atteignent plus.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Si vous gérez un commerce de proximité et que vous cherchez par où commencer concrètement, notre guide sur l'<Link to="/outil-fidelisation-petit-commerce" className="text-[#0c97a9] hover:underline font-semibold">outil de fidélisation pour petit commerce</Link> vous donne un plan en 3 étapes avec des solutions accessibles dès 0 €.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Créez votre première carte sur Unotif gratuitement et envoyez votre premier push en moins d'une heure.
            </p>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default NotificationsPushWallet;
